import { useState } from "react";
import { ExpiringCertsCard } from "@/components/dashboard/ExpiringCertsCard";
import { HeadcountTrendCard } from "@/components/dashboard/HeadcountTrendCard";
import { OrgHealthFilterBar } from "@/components/dashboard/OrgHealthFilterBar";
import { OrgHealthTiles } from "@/components/dashboard/OrgHealthTiles";
import { useSetBreadcrumbs } from "@/components/shared/breadcrumbs";
import { PageHeader } from "@/components/shared/page-header";
import {
  EMPTY_ORG_HEALTH_FILTERS,
  filterSearchParams,
  type OrgHealthFilters,
  useHeadcountTrend,
  useOrgHealthOverview,
} from "@/hooks/use-org-health";
import { pageOriginSearch } from "@/lib/detail-origin";

/**
 * Initial filters from the URL query string so filtered views are shareable
 * (same window.location pattern as the list deep-links — no router search
 * validation involved).
 */
function filtersFromUrl(): OrgHealthFilters {
  const params = new URLSearchParams(window.location.search);
  return {
    pillarId: params.get("pillarId"),
    departmentId: params.get("departmentId"),
    roleId: params.get("roleId"),
    // No allow-list: employment types are admin-managed config (EPIC-028), so
    // any key is accepted here and simply matches no rows if it isn't real.
    employmentTypes: (params.get("employmentTypes") ?? "").split(",").filter(Boolean),
  };
}

/**
 * Admin-only organization dashboard: HR analytics (headcount over time) plus
 * the org-health tiles and org-wide expiring certifications. Every widget
 * respects the filter bar (pillar / department / role / employment types);
 * active filters live in the URL so a filtered view can be shared.
 */
const DEFAULT_MONTHS = 12;
const MONTH_CHOICES = [12, 36, 60];

function monthsFromUrl(): number {
  const raw = Number(new URLSearchParams(window.location.search).get("months"));
  return MONTH_CHOICES.includes(raw) ? raw : DEFAULT_MONTHS;
}

export function OrgHealthDashboard() {
  useSetBreadcrumbs([{ label: "Administration" }, { label: "Org Health" }]);
  const [filters, setFilters] = useState<OrgHealthFilters>(() =>
    typeof window === "undefined" ? EMPTY_ORG_HEALTH_FILTERS : filtersFromUrl(),
  );
  const [months, setMonths] = useState<number>(() =>
    typeof window === "undefined" ? DEFAULT_MONTHS : monthsFromUrl(),
  );

  const overviewQuery = useOrgHealthOverview(filters);
  const trendQuery = useHeadcountTrend(filters, months);
  const overview = overviewQuery.data;

  function syncUrl(nextFilters: OrgHealthFilters, nextMonths: number) {
    const params = filterSearchParams(nextFilters);
    if (nextMonths !== DEFAULT_MONTHS) params.set("months", String(nextMonths));
    const qs = params.toString();
    window.history.replaceState(null, "", qs ? `?${qs}` : window.location.pathname);
  }

  function applyFilters(next: OrgHealthFilters) {
    setFilters(next);
    syncUrl(next, months);
  }

  function applyMonths(next: number) {
    setMonths(next);
    syncUrl(filters, next);
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title="Org Health"
        description="Company-wide workforce and certification signal."
      />

      {/* Headline numbers first (still filter-aware), then the filter row,
          then the two charts side by side on wide screens. */}
      <OrgHealthTiles
        orgHealth={overview?.orgHealth}
        isLoading={overviewQuery.isLoading}
        isError={overviewQuery.isError}
        onRetry={() => overviewQuery.refetch()}
      />

      <OrgHealthFilterBar filters={filters} onChange={applyFilters} />

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        <HeadcountTrendCard
          points={trendQuery.data?.points}
          months={months}
          onMonthsChange={applyMonths}
          isLoading={trendQuery.isLoading}
          isError={trendQuery.isError}
          onRetry={() => trendQuery.refetch()}
        />

        <ExpiringCertsCard
          title="Certifications across the company"
          insight={overview?.expiringCerts}
          isLoading={overviewQuery.isLoading}
          isError={overviewQuery.isError}
          onRetry={() => overviewQuery.refetch()}
          personLinkSearch={pageOriginSearch("org-health")}
        />
      </div>
    </div>
  );
}
